import { motion } from 'framer-motion';
import { UserCircle, Briefcase, Image, Award, GraduationCap, DollarSign, CreditCard } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import PageHero from '../components/PageHero';
import { SF_UI } from '../components/ui/tokens';

const FundingPage = () => {
  const fundingSteps = [
    {
      icon: <UserCircle size={40} />,
      title: 'Become Athlete Professional',
      description: 'Complete your athlete verification process and establish your professional profile with validated credentials and performance history.',
      delay: 0
    },
    {
      icon: <Image size={40} />,
      title: 'Creation NFT Marketplace',
      description: 'Create and sell unique digital collectibles that showcase your achievements, enabling fans to support your journey while owning a piece of your athletic legacy.',
      delay: 0.1,
      isClickable: true,
      url: 'https://openplaza.io'
    },
    {
      icon: <Briefcase size={40} />,
      title: 'Build Your Brand',
      description: 'Utilize our platform tools to develop your personal brand, grow your audience, and increase your visibility to potential sponsors and supporters.',
      delay: 0.2,
      isClickable: true,
      url: 'https://lnkhub.app/refer/DPUPHP'
    },
    {
      icon: <GraduationCap size={40} />,
      title: (
        <div className="flex items-center">
          Receive Scholarships
          <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
            Coming Soon
          </span>
        </div>
      ),
      description: 'Access our diverse range of scholarship opportunities tailored to your sport, level, and specific needs to fuel your athletic journey.',
      delay: 0.3,
      comingSoon: true
    }
  ];

  const fundingOptions = [
    {
      icon: <Award size={40} />,
      title: 'Merit Scholarships',
      description: 'Performance-based funding awarded to athletes who demonstrate exceptional skill and potential in their respective sports.',
      color: 'bg-primary'
    },
    {
      icon: <DollarSign size={40} />,
      title: 'Community Funding',
      description: 'Direct support from fans and community members who believe in your potential and want to contribute to your success.',
      color: 'bg-secondary'
    },
    {
      icon: <CreditCard size={40} />,
      title: 'NFT Revenue',
      description: 'Generate ongoing income through the creation and sale of digital collectibles representing your achievements and milestones.',
      color: 'bg-primary-600'
    }
  ];

  return (
    <>
      <PageHero
        eyebrow="Funding"
        title="Funding Your Athletic Journey"
        subtitle="SportsFuture provides innovative funding solutions that empower athletes to focus on their performance while securing their financial future."
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {fundingSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: step.delay }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } gap-8 items-center`}
              >
                <div className="md:w-1/4 flex justify-center">
                  <div className={`w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center text-primary ${
                    step.comingSoon ? 'opacity-50' : ''
                  }`}>
                    {step.icon}
                  </div>
                </div>
                <div className="md:w-3/4">
                  {step.isClickable ? (
                    <a
                      href={step.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system hover:shadow-lg transition-all duration-300 hover:bg-primary-50 cursor-pointer border-2 border-transparent hover:border-primary"
                    >
                      <h3 className="text-xl font-semibold mb-3 text-primary hover:text-primary-600 transition-colors">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                      <div className="mt-3 text-primary text-sm font-medium">
                        {step.url === 'https://openplaza.io' ? 'Visit NFT Marketplace →' : 'Visit LinkHUB →'}
                      </div>
                    </a>
                  ) : (
                    <div className={`bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system ${
                      step.comingSoon ? 'opacity-50' : ''
                    }`}>
                      <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section bgColor="bg-primary-50" eyebrow="Options" title="Funding Options" centered>
        <div className="max-w-4xl mx-auto">
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {fundingOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
              >
                <div className="flex justify-center mb-4">
                  <div className={`w-16 h-16 ${option.color} rounded-full flex items-center justify-center`}>
                    <div className="text-white">
                      {option.icon}
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-center mb-2">{option.title}</h3>
                <p className="text-gray-600 text-center">
                  {option.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Funding Benefits</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Focus on Performance</h3>
              <p className="text-gray-600">
                Our funding solutions allow you to concentrate on your athletic development 
                by reducing financial barriers and covering essential expenses like training, 
                equipment, and competition fees.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Global Opportunities</h3>
              <p className="text-gray-600">
                Access to funding that enables participation in international competitions, 
                training camps, and specialized coaching to elevate your performance on the 
                global stage.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Career Sustainability</h3>
              <p className="text-gray-600">
                Build long-term financial stability through our innovative NFT marketplace 
                and community funding models that continue generating revenue throughout your 
                athletic career.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border-l-4 border-primary"
            >
              <h3 className="text-lg font-semibold mb-2">Path to Professionalism</h3>
              <p className="text-gray-600">
                Structured support to help promising athletes bridge the gap from amateur to 
                professional status with targeted funding and development resources tailored 
                to your specific needs.
              </p>
            </motion.div>
          </div>
          
          <div className="text-center">
            <Button to="/apply" variant="primary" size="lg">
              Apply for Funding
            </Button>
            <p className="mt-4 text-gray-600">
              Ready to take your athletic career to the next level? Apply now to explore 
              our funding options and join the SportsFuture community.
            </p>
          </div>
        </div>
      </Section>
    </>
  );
};

export default FundingPage;
